/**
 * Magic Link Verification Page
 * Handles the magic link click from email
 */

import { useCallback, useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Capacitor } from '@capacitor/core'
import { useSession } from '@/features/auth/hooks/useSession'
import { env } from '@/lib/env'
import { getAuthItem, setAuthItem } from '@/lib/authStorage'
import { ConnectionErrorScreen } from '@/common/components/ConnectionErrorScreen'
import { XaviorLoader } from '@/common/components/XaviorLoader'
import { hapticSuccess, hapticError } from '@/lib/capacitor'
import { useShouldPauseForBanner, dismissAppBanner, onContinueInBrowser } from '@/common/components/OpenInAppBanner'

const API_BASE_URL = env.apiBaseUrl
const AUTH_BASE_URL = env.authBaseUrl

function safeParseJson(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

function extractErrorMessage(data: unknown, rawText?: string): string | null {
  // If data is already a string (plain text response), return it
  if (typeof data === 'string' && data.trim()) return data.trim()
  // Try to extract from JSON response
  if (data && typeof data === 'object') {
    const record = data as Record<string, unknown>
    if (typeof record.error === 'string') return record.error
    if (typeof record.message === 'string') return record.message
    if (typeof record.error === 'object' && record.error) {
      const nested = record.error as Record<string, unknown>
      if (typeof nested.message === 'string') return nested.message
    }
  }
  // Fall back to raw response text (handles plain text error responses from Go)
  if (rawText && rawText.trim()) return rawText.trim()
  return null
}

function formatAuthError(status: number, data: unknown, rawText?: string): string {
  if (status === 429) {
    return 'Too many attempts. Please wait a moment and try again.'
  }
  if (status === 502 || status === 503 || status === 504) {
    return 'Login service is temporarily unavailable. Please try again.'
  }
  return extractErrorMessage(data, rawText) || 'Failed to verify magic link'
}

function formatNetworkError(error: unknown): string {
  if (error instanceof TypeError) {
    return 'Network error while verifying your magic link. Please check your connection and try again.'
  }
  if (error instanceof Error && error.message) {
    return error.message
  }
  return 'Failed to verify magic link'
}

function shouldShowConnectionScreen(status?: number, error?: unknown): boolean {
  if (typeof status === 'number' && status >= 500) {
    return true
  }
  if (error instanceof TypeError) {
    return true
  }
  if (error instanceof Error) {
    const message = error.message.toLowerCase()
    return (
      message.includes('failed to fetch') ||
      message.includes('network') ||
      message.includes('timed out') ||
      message.includes('timeout')
    )
  }
  return false
}

export function MagicLinkVerify() {
  const navigate = useNavigate()
  const { setToken, waitForProfile } = useSession()
  const [status, setStatus] = useState<'verifying' | 'success' | 'error' | 'waiting'>('verifying')
  const [error, setError] = useState('')
  const hasVerified = useRef(false)
  const [connectionError, setConnectionError] = useState<Error | null>(null)
  const [isReturningUser, setIsReturningUser] = useState(false)

  // Check if we should pause for the "Open in App" banner
  const shouldPauseForBanner = useShouldPauseForBanner()

  const verifyToken = useCallback(async (force?: boolean) => {
    // Prevent double verification in React StrictMode
    if (hasVerified.current && !force) return
    hasVerified.current = true
    setStatus('verifying')
    setError('')
    setConnectionError(null)

      // Get token and email from URL query params
      const params = new URLSearchParams(window.location.search)
      const token = params.get('token')
      const email = params.get('email')

      if (!token) {
        setStatus('error')
        setError('No verification token found in URL')
        return
      }

      try {
        // Include email in both header (for supervisor routing) and body (for workspace)
        const headers: Record<string, string> = { 'Content-Type': 'application/json' }
        if (email) {
          headers['X-Auth-Email'] = email
        }
        // Tell backend this is a native app so it returns refresh token in body
        if (Capacitor.isNativePlatform()) {
          headers['X-Client-Platform'] = 'capacitor'
        }
        const verifyUrl = `${API_BASE_URL}/auth/verify`
        console.log('[MagicLinkVerify] Calling:', verifyUrl, { token: token?.slice(0, 10) + '...', email })
        const fetchOptions: RequestInit = {
          method: 'POST',
          headers,
          body: JSON.stringify({ token, email }),
        }
        // CapacitorHttp doesn't support credentials: 'include' and may hang.
        // Native uses Bearer token auth, not cookies, so credentials aren't needed.
        if (!Capacitor.isNativePlatform()) {
          fetchOptions.credentials = 'include'
        }
        const response = await fetch(verifyUrl, fetchOptions)
        console.log('[MagicLinkVerify] Response status:', response.status)

        const responseText = await response.text()
        const responseData = responseText ? safeParseJson(responseText) : null

        if (!response.ok) {
          const errorMessage = formatAuthError(response.status, responseData, responseText)
          // Handle "already used" errors
          if (errorMessage.includes('already used')) {
            console.log('[MagicLinkVerify] Token already used')
            // Check if we already have a session (from a successful previous request)
            const existingToken = getAuthItem('xavior_auth_token')
            if (existingToken) {
              // We have a session, this was a duplicate call from React StrictMode
              console.log('[MagicLinkVerify] Found existing session, treating as success')
              setStatus('success')
              // Post-auth → app root (PDR 010); the gate chain decides what shows.
              setTimeout(() => navigate('/'), 800)
            } else {
              // Token was used but we don't have a session - user needs to request a new link
              console.log('[MagicLinkVerify] No session found, magic link expired or used elsewhere')
              void hapticError()
              setStatus('error')
              setError('This magic link has already been used. Please request a new one.')
            }
            return
          }
          if (shouldShowConnectionScreen(response.status)) {
            const detail = extractErrorMessage(responseData, responseText) || response.statusText || errorMessage
            setConnectionError(new Error(`HTTP ${response.status}: ${detail}`))
            setStatus('error')
            return
          }
          throw new Error(errorMessage)
        }

        if (!responseData || typeof responseData !== 'object') {
          throw new Error('Unexpected response from login service.')
        }

        const data = responseData as {
          accessToken?: string
          sessionToken?: string
          userId: string
          email: string
          refreshToken?: string
        }

        // Prefer the JWT (`accessToken`) over the legacy hex `sessionToken`.
        // The supervisor validates JWTs statelessly against JWT_SECRET, so
        // they survive supervisor restarts. The legacy sessionToken relies
        // on supervisor's in-memory MemoryStore (session/store.go) which is
        // wiped on every restart and never shared with api-staging — so any
        // /chat request after a supervisor restart 401s with "invalid jwt:
        // token contains an invalid number of segments". Falling back to
        // sessionToken keeps the legacy path working only if the server has
        // ENABLE_LEGACY_SESSIONS=true AND supervisor's in-memory store still
        // has the token.
        const authToken = data.accessToken || data.sessionToken
        if (!authToken) {
          throw new Error('Auth response missing both accessToken and sessionToken.')
        }

        // Fire warmup immediately with the new token — gives a head start
        // while React state updates propagate. SessionProvider will also fire
        // warmup but the supervisor handles concurrent calls fine.
        void fetch(`${AUTH_BASE_URL}/warmup`, {
          method: 'POST',
          ...(Capacitor.isNativePlatform() ? {} : { credentials: 'include' as const }),
          headers: { Authorization: `Bearer ${authToken}` },
        }).catch(() => {})

        // Store auth token (uses localStorage on native, sessionStorage on web)
        setAuthItem('xavior_auth_token', authToken)
        if (data.sessionToken) {
          setAuthItem('sessionToken', data.sessionToken)
        }
        setAuthItem('userId', data.userId)
        setAuthItem('userEmail', data.email)

        // Store refresh token for native apps (persists across force-quit/upgrade)
        if (Capacitor.isNativePlatform() && data.refreshToken) {
          setAuthItem('xavior_refresh_token', data.refreshToken)
          console.log('[MagicLinkVerify] Stored refresh token in localStorage for native persistence')
        }

        // Update session provider
        setToken(authToken)

        // Wait for the SessionProvider to actually fetch and validate the profile
        // This prevents race conditions where we navigate before profile loads
        console.log('[MagicLinkVerify] Waiting for profile to load...')
        const loadedProfile = await waitForProfile()
        console.log('[MagicLinkVerify] Profile loaded:', loadedProfile)

        if (!loadedProfile) {
          setStatus('error')
          setError('Failed to load profile. Please try logging in again.')
          return
        }

        // Determine if this is a returning user (has paid or started a trial before)
        const returning = loadedProfile.isPaidAccount || loadedProfile.trialUsed
        setIsReturningUser(returning)
        setStatus('success')

        // Haptic feedback on success
        void hapticSuccess()

        // Post-auth → app root (PDR 010); the gate chain decides what shows.
        setTimeout(() => {
          navigate('/')
        }, 800)

      } catch (err: unknown) {
        console.error('Verification error:', err)

        // Haptic feedback on error
        void hapticError()

        if (shouldShowConnectionScreen(undefined, err)) {
          const message = formatNetworkError(err)
          setConnectionError(err instanceof Error ? err : new Error(message))
          setStatus('error')
          return
        }
        setStatus('error')
        setError(formatNetworkError(err))
      }
  }, [setToken, waitForProfile, navigate])

  // Start verification - pause if banner is showing on mobile web
  useEffect(() => {
    if (shouldPauseForBanner) {
      // On mobile web, wait for user to choose "Continue in Browser"
      // The OpenInAppBanner will handle "Open App" option
      setStatus('waiting')
      return
    }
    verifyToken()
  }, [verifyToken, shouldPauseForBanner])

  // Listen for "Continue in Browser" event from the banner
  useEffect(() => {
    if (!shouldPauseForBanner) return

    const cleanup = onContinueInBrowser(() => {
      console.log('[MagicLinkVerify] Received continue-in-browser event')
      setStatus('verifying')
      void verifyToken(true)
    })

    return cleanup
  }, [verifyToken, shouldPauseForBanner])

  if (connectionError) {
    return (
      <ConnectionErrorScreen
        error={connectionError}
        onRetry={() => {
          setConnectionError(null)
          void verifyToken(true)
        }}
      />
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-5" style={{ background: '#f8f7f5' }}>
      <div className="bg-white rounded-2xl p-12 max-w-md w-full border" style={{ borderColor: '#e8e6e2' }}>
        <div className="text-5xl font-bold text-center text-gray-900 mb-8">XAVIOR</div>

        {status === 'waiting' && (
          <div className="text-center">
            <div className="text-gray-600 text-6xl mb-4">📱</div>
            <p className="text-gray-900 font-semibold text-lg mb-2">Open in app?</p>
            <p className="text-gray-600 text-sm mb-6">Choose how to continue with your magic link</p>
          </div>
        )}

        {status === 'verifying' && (
          <div className="text-center">
            <XaviorLoader size="lg" className="mx-auto mb-4" />
            <p className="text-gray-600">Verifying your magic link...</p>
          </div>
        )}

        {status === 'success' && (
          <div className="text-center">
            <div className="text-green-600 text-6xl mb-4">✓</div>
            <p className="text-gray-900 font-semibold text-lg mb-2">
              {isReturningUser ? 'Welcome back!' : 'Welcome!'}
            </p>
            <p className="text-gray-600">Redirecting to your workspace...</p>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center">
            <div className="text-amber-600 text-6xl mb-4">⚠</div>
            <p className="text-gray-900 font-semibold text-lg mb-2">Verification Failed</p>
            <p className="text-gray-600 text-sm mb-6">{error}</p>
            <button
              onClick={() => verifyToken(true)}
              className="w-full py-3.5 px-6 text-white font-medium rounded-xl mb-3 text-sm"
              style={{ background: '#0ea5e9', transition: 'background 0.15s ease' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = '#0284c7' }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = '#0ea5e9' }}
            >
              Try Again
            </button>
            <button
              onClick={() => navigate('/login')}
              className="w-full py-3.5 px-6 bg-gray-50 hover:bg-gray-100 text-gray-700 font-medium rounded-xl text-sm border"
              style={{ borderColor: '#e8e6e2' }}
            >
              Back to Login
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
